import React, { useState, useEffect } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { productsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../utils/currency';

export function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [editingCost, setEditingCost] = useState(null);
  const [costValue, setCostValue] = useState('');

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getAll();
      setProducts(response.data.products);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await productsAPI.sync();
      toast.success(`Synced ${response.data.synced} products!`);
      fetchProducts();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateCost = async (productId) => {
    try {
      await productsAPI.updateCost(productId, parseFloat(costValue));
      toast.success('Cost price updated!');
      setEditingCost(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update cost');
    }
  };

  // ── Filter by search (title, SKU, variant title) ──────────────────────────
  const filteredProducts = products.filter(p =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase()) ||
    p.variant_title?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Group variants under parent product title ─────────────────────────────
  // Each DB row is already one variant — group them so variants sit together
  const grouped = filteredProducts.reduce((acc, product) => {
    const key = product.shopify_product_id || product.title;
    if (!acc[key]) acc[key] = [];
    acc[key].push(product);
    return acc;
  }, {});

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Products</h1>
          <p className="text-gray-600">{products.length} total variants</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Sync from Shopify'}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product name, variant or SKU..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">Variant</th>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">SKU</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Price</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Stock</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <RefreshCw className="animate-spin text-blue-600 mx-auto mb-2" size={24} />
                    <p className="text-gray-500">Loading products...</p>
                  </td>
                </tr>
              ) : Object.values(grouped).length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                Object.values(grouped).map((variants) => (
                  variants.map((product, variantIdx) => (
                    <tr
                      key={product.id}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        variantIdx === 0 && variants.length > 1 ? 'border-t-2 border-t-gray-200' : ''
                      }`}
                    >
                      {/* Product title — only show on first variant row */}
                      <td className="py-4 px-6">
                        {variantIdx === 0 ? (
                          <p className="font-medium text-gray-800">{product.title}</p>
                        ) : (
                          <p className="text-gray-300 text-xs pl-2">↳</p>
                        )}
                      </td>

                      {/* Variant title */}
                      <td className="py-4 px-6 text-sm text-gray-500">
                        {product.variant_title && product.variant_title !== 'Default Title'
                          ? product.variant_title
                          : <span className="text-gray-300">—</span>}
                      </td>

                      {/* SKU */}
                      <td className="py-4 px-6">
                        {product.sku ? (
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {product.sku}
                          </span>
                        ) : (
                          <span className="text-gray-300">N/A</span>
                        )}
                      </td>

                      <td className="py-4 px-6 text-right font-semibold text-blue-600">
                        {formatCurrency(product.price)}
                      </td>

                      <td className="py-4 px-6 text-right">
                        {editingCost === product.id ? (
                          <input
                            type="number"
                            step="0.01"
                            value={costValue}
                            onChange={(e) => setCostValue(e.target.value)}
                            onBlur={() => handleUpdateCost(product.id)}
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateCost(product.id)}
                            className="w-24 px-2 py-1 border border-blue-500 rounded text-right focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <button
                            onClick={() => { setEditingCost(product.id); setCostValue(product.cost_price || '0'); }}
                            className="text-gray-700 hover:text-blue-600 font-medium"
                          >
                            {formatCurrency(product.cost_price || 0)}
                          </button>
                        )}
                      </td>

                      <td className="py-4 px-6 text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          product.inventory_quantity === 0   ? 'bg-red-100 text-red-700' :
                          product.inventory_quantity <= 10  ? 'bg-yellow-100 text-yellow-700' :
                                                              'bg-green-100 text-green-700'
                        }`}>
                          {product.inventory_quantity}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right font-semibold text-green-600">
                        {formatCurrency(parseFloat(product.price) - parseFloat(product.cost_price || 0))}
                      </td>
                    </tr>
                  ))
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ProductList;